import { useEffect, useState } from 'react';
import I18n from 'I18n';
import { ThemeProvider } from 'styled-components';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { debugLog } from 'video-embed/utils/debug';
import { hasParentWithClass } from 'video-embed/utils/dom';
import SimpleSelect from './SimpleSelect';
import theme from '../theme';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PLAYBACK_SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];
const PlaybackOptionsPopover = ({
  player,
  hls,
  tracker
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(String(player.playbackRate()));
  const [hlsLevel, setHlsLevel] = useState(hls ? hls.currentLevel : null);
  const [hlsLevelHasChanged, setHlsLevelHasChanged] = useState(false);
  useEffect(() => {
    player.on('ratechange', () => {
      const rate = String(player.playbackRate());
      if (rate !== playbackRate) {
        debugLog(`Player playbackRate switched to: ${rate}`);
        setPlaybackRate(rate);
      }
    });
    if (!hls) {
      return;
    }
    hls.on('hlsLevelSwitched', (_hls, data) => {
      const levelDetail = hls.levels[data.level];
      if (levelDetail) {
        debugLog(`HLS level updated to ${levelDetail.height}p`, data);
      }
      setHlsLevel(data.level);
    });
  }, [hls, player, playbackRate]);
  useEffect(() => {
    const handleBlur = e => {
      if ((e.key === 'Tab' || e.key === 'Escape') && isOpen) {
        if (document.activeElement && hasParentWithClass(document.activeElement, 'playback-options-content')) {
          return;
        }
        setIsOpen(false);
        tracker.track('playerInteraction', {
          action: 'blur-playback-options'
        });
      }
    };
    const handleClick = e => {
      if (hasParentWithClass(e.target, 'playback-options-content') || hasParentWithClass(e.target, 'vjs-playback-options')) {
        return;
      }
      if (isOpen) {
        setIsOpen(false);
        tracker.track('playerInteraction', {
          action: 'close-playback-options-outside-click'
        });
      }
    };
    player.on('keyup', handleBlur);
    player.on('click', handleClick);
    return () => {
      player.off('keyup', handleBlur);
      player.off('click', handleClick);
    };
  }, [player, tracker, isOpen]);
  const renderPlaybackRate = () => {
    const playbackSpeedOptions = PLAYBACK_SPEEDS.map(speed => ({
      text: speed === 1 ? I18n.text('videoPlayer.playbackOptions.normal') : `${speed}x`,
      value: String(speed)
    }));
    const selectedOption = playbackSpeedOptions.find(o => o.value === String(playbackRate));
    if (selectedOption) {
      return /*#__PURE__*/_jsxs("div", {
        style: {
          marginBottom: hls ? '1rem' : 0,
          whiteSpace: 'nowrap'
        },
        children: [/*#__PURE__*/_jsx("label", {
          htmlFor: "playback speed",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "videoPlayer.playbackOptions.playbackRate"
          })
        }), /*#__PURE__*/_jsx(SimpleSelect, {
          name: "playback speed",
          options: playbackSpeedOptions,
          value: selectedOption.value,
          onChange: e => {
            player.playbackRate(Number(e.target.value));
            tracker.track('playerInteraction', {
              action: 'change-playback-speed'
            });
          }
        })]
      });
    }
    return null;
  };
  const renderQualitySelect = () => {
    if (!(hls && hls.levels && hls.levels.length > 0 && typeof hlsLevel === 'number')) {
      return null;
    }
    const qualityLevelOptions = hls.levels.map((level, i) => {
      const isSelected = i === hlsLevel;
      const text = isSelected && !hlsLevelHasChanged ? `${level.height}p ${I18n.text('videoPlayer.playbackOptions.auto')}` : `${level.height}p`;
      return {
        text,
        value: String(i)
      };
    });
    return /*#__PURE__*/_jsxs("div", {
      style: {
        whiteSpace: 'nowrap'
      },
      children: [/*#__PURE__*/_jsx("label", {
        htmlFor: "quality",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "videoPlayer.playbackOptions.quality"
        })
      }), /*#__PURE__*/_jsx(SimpleSelect, {
        name: "quality",
        value: hlsLevel.toString(),
        options: qualityLevelOptions,
        onChange: e => {
          const currentLevel = Number(e.target.value);
          hls.currentLevel = currentLevel;
          setHlsLevel(currentLevel);
          setHlsLevelHasChanged(true);
          tracker.track('playerInteraction', {
            action: 'change-quality'
          });
        }
      })]
    });
  };
  const renderContent = () => {
    return /*#__PURE__*/_jsxs("div", {
      className: "playback-options-content vjs-menu-content",
      style: {
        minWidth: 250
      },
      children: [renderPlaybackRate(), renderQualitySelect(), /*#__PURE__*/_jsx("div", {
        className: "popover-triangle"
      })]
    });
  };
  const controlBarButtonEl = /*#__PURE__*/_jsx("button", {
    className: "vjs-control vjs-button vjs-menu-button",
    type: "button",
    title: I18n.text('videoPlayer.playbackOptions.buttonTitle'),
    onClick: () => {
      setIsOpen(!isOpen);
      const action = isOpen ? 'close-playback-options-button' : 'open-playback-options';
      tracker.track('playerInteraction', {
        action
      });
    },
    children: /*#__PURE__*/_jsx("span", {
      className: "vjs-icon-cog vjs-icon-placeholder"
    })
  });
  return /*#__PURE__*/_jsxs(ThemeProvider, {
    theme: theme,
    children: [controlBarButtonEl, isOpen && renderContent()]
  });
};
export default PlaybackOptionsPopover;